import {Button, Divider, Stack, Typography} from '@mui/material';
import {BankIDLogo, ResursLogo, ResursLoading} from '@resurs-internal/mui';
import {BankIDQRCode} from '../bankid/BankIDQRCode';
import {HowToSignToggle} from './HowToSignToggle';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslate} from '../../hooks';
import {
  bankidIntentSuffix,
  chromeIOSURL,
  chromeURL,
  edgeURL,
  firefoxURL,
  isAndroid,
  isChrome,
  isChromeWebView,
  isEdge,
  isFirefox,
  isIOS,
  isLinux,
  isSafari,
  isSamsung,
  samsungURL,
} from '../../services/browser';

interface SigningViewProps {
  qrCode?: string;
  isLoading: boolean;
  autoStartToken?: string;
  hintCode?: string;
  onCancel: () => void;
  expiresAt?: string;
}

function getSecondsRemaining(expiresAt?: string): number {
  if (!expiresAt) return Number.NaN;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(diffMs / 1000));
}

function formatTimeLeft(
  totalSeconds: number,
  t: ReturnType<typeof useTranslate>,
): string {
  if (Number.isNaN(totalSeconds)) return '';

  if (totalSeconds < 0) totalSeconds = 0;

  const minutes = Math.ceil(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // console.log(
  //   'totalSeconds: ' +
  //     totalSeconds +
  //     ', minutes:' +
  //     minutes +
  //     ', seconds:' +
  //     seconds,
  // );

  if (totalSeconds < 60) {
    return `${seconds} ${t('signature.countdown.seconds.left')}`;
  }

  // Special case: keep displaying '2 minutes left', until seconds only display kick in
  // otherwise there is a brief second or so of displaying '1 minutes left'
  return `${minutes == 1 ? 2 : minutes} ${t('signature.countdown.minutes.left')}`;
}

export const SigningView: React.FC<SigningViewProps> = ({
  qrCode,
  isLoading,
  autoStartToken,
  hintCode,
  onCancel,
  expiresAt,
}) => {
  const t = useTranslate();

  const bankIDURL = useMemo(() => {
    if (isIOS && isSafari) {
      // Use bankid:/// with no redirect so Safari stays at the auth page. After
      // BankID auth its done screen is shown; the user returns to Safari where the
      // polling tab is still active and completes auth.
      return `bankid:///?autostarttoken=${autoStartToken}`;
    }
    if (isIOS && isChrome) {
      // Use bankid:/// (not https://app.bankid.com/) so Chrome does not navigate
      // away from the auth page — custom schemes are not navigable by Chrome, so
      // the tab stays at its current URL. redirect=googlechromes:// (bare, no URL)
      // simply brings Chrome to the foreground without opening a new tab or
      // navigating anywhere, so the original polling tab resumes and completes auth.
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeIOSURL}`;
    }
    if (isIOS && isFirefox) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${encodeURIComponent(`firefox://open-url?url=${encodeURIComponent(window.location.href)}`)}`;
    }
    if (isIOS) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
    }
    // Chrome WebView cannot resolve Android App Links — use bankid:// scheme.
    if (isChromeWebView) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
    }
    // Edge on Android does not resolve App Links and blocks bankid:// — use
    // the Android Intent URL.
    if (isAndroid && isEdge) {
      return `intent:///?autostarttoken=${autoStartToken}&redirect=null${bankidIntentSuffix}`;
    }
    if (isAndroid) {
      return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
    }
    if (isChrome) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeURL}`;
    }
    if (isEdge) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${edgeURL}`;
    }
    if (isFirefox) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${firefoxURL}`;
    }
    if (isSamsung) {
      return `bankid:///?autostarttoken=${autoStartToken}&redirect=${samsungURL}`;
    }
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
  }, [autoStartToken]);

  const isUserSigning = hintCode === 'userSign';
  const [openingBankId, setOpeningBankId] = useState(false);

  const [secondsLeft, setSecondsLeft] = useState(() =>
    getSecondsRemaining(expiresAt),
  );

  const [hideCountdown, setHideCountdown] = useState(false);

  useEffect(() => {
    let hideCountdownTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        hideCountdownTimeoutId = setTimeout(() => setHideCountdown(true), 5000);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(hideCountdownTimeoutId);
    };
  }, [expiresAt]);

  const timeLeftLabel = expiresAt ? formatTimeLeft(secondsLeft, t) : '';

  const handleOpenBankId = () => {
    setOpeningBankId(true);
    window.location.href = bankIDURL;
  };

  if (openingBankId) {
    return (
      <Stack spacing={2} alignItems={'center'} width={'100%'} px={3} py={2}>
        <ResursLogo variant={'text'} width={120} />
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'baseline'}
          width={'100%'}>
          <Typography variant={'h2'} component={'h1'}>
            {t('signature.bankid.title')}
          </Typography>
          <Button
            variant={'text'}
            onClick={onCancel}
            sx={{minWidth: 'auto', flexShrink: 0}}>
            {t('common.cancel')}
          </Button>
        </Stack>
        <Stack spacing={2} alignItems={'center'} py={4}>
          <ResursLoading size={'xl'} />
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <BankIDLogo size={'sm'} />
            <Typography variant={'body1'}>
              {t('signature.bankid.openingBankId')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  }
  return (
    <Stack spacing={2} alignItems={'center'} width={'100%'} px={3} py={2}>
      <ResursLogo variant={'text'} width={120} />
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'baseline'}
        width={'100%'}>
        <Typography variant={'h2'} component={'h1'}>
          {t('signature.bankid.title')}
        </Typography>
        <Button
          variant={'text'}
          onClick={onCancel}
          sx={{minWidth: 'auto', flexShrink: 0}}>
          {t('common.cancel')}
        </Button>
      </Stack>
      {isUserSigning ? (
        <Stack spacing={2} alignItems={'center'}>
          <Typography variant={'body1'} textAlign={'center'}>
            {t('signature.bankid.userSign.subtitle')}
          </Typography>
          <ResursLoading size={'xl'} />
        </Stack>
      ) : (
        <>
          <Typography variant={'body1'} textAlign={'center'}>
            {t('signature.bankid.subtitle')}
          </Typography>
          <BankIDQRCode isLoading={isLoading || !qrCode} code={qrCode ?? ''} />
          {!hideCountdown && expiresAt && (
            <Typography
              variant={'body2'}
              textAlign={'center'}
              color={'text.secondary'}>
              {timeLeftLabel || '\u00A0'}
            </Typography>
          )}
        </>
      )}
      <Stack direction={'column'} spacing={2} width={'100%'}>
        <Divider>{t('common.or')}</Divider>
        <Button
          variant={'outlined'}
          disabled={!autoStartToken || isUserSigning || isLinux}
          onClick={handleOpenBankId}
          startIcon={<BankIDLogo size={'sm'} />}
          sx={{borderRadius: 2, py: 1.5}}>
          {t('signature.bankid.button')}
        </Button>
        {isLinux && (
          <Typography
            variant={'body2'}
            textAlign={'center'}
            color={'text.secondary'}>
            {t('common.bankid.linux-hint')}
          </Typography>
        )}
        {!isUserSigning && <HowToSignToggle />}
      </Stack>
    </Stack>
  );
};
